/* proforma-invoice.js
   - Renders printable HTML into a same-origin iframe
   - Waits for fonts + images
   - Uses html2canvas + jsPDF (multi-page A4)
   - Fallback: dom-to-image-more + jsPDF, then simple print
*/

(function () {
  'use strict';

  /* ---------- Utilities ---------- */
  function loadScript(url, timeout = 8000) {
    return new Promise((resolve, reject) => {
      if (!url) return reject(new Error('No URL'));
      if (document.querySelector(`script[src="${url}"]`)) return resolve();
      const s = document.createElement('script');
      s.src = url;
      s.async = true;
      let done = false;
      const timer = setTimeout(() => {
        if (done) return;
        done = true;
        reject(new Error('Script load timed out: ' + url));
      }, timeout);
      s.onload = () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        resolve();
      };
      s.onerror = () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        reject(new Error('Script load error: ' + url));
      };
      document.head.appendChild(s);
    });
  }

  function $(id) {
    return document.getElementById(id) || null;
  }

  function escapeHtml(str) {
    if (typeof str !== 'string') return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function show(el) { if (el) el.style.display = ''; }
  function hide(el) { if (el) el.style.display = 'none'; }

  // Wait for images inside a document/element
  function waitForImagesInDoc(docOrEl, timeout = 4000) {
    const container =
      (docOrEl && docOrEl.querySelector)
        ? docOrEl
        : (docOrEl && docOrEl.body)
        ? docOrEl.body
        : document;

    const imgs = Array.from(container.querySelectorAll('img'));
    if (!imgs.length) return Promise.resolve();

    return Promise.all(
      imgs.map(img => new Promise(res => {
        if (img.complete && img.naturalWidth !== 0) return res();
        let done = false;
        const t = setTimeout(() => {
          if (done) return;
          done = true;
          res();
        }, timeout);
        img.onload = img.onerror = () => {
          if (done) return;
          done = true;
          clearTimeout(t);
          res();
        };
      }))
    );
  }

  async function waitForFontsInDoc(doc, timeout = 4000) {
    try {
      if (doc && doc.fonts && doc.fonts.ready) {
        const p = doc.fonts.ready;
        await Promise.race([p, new Promise(r => setTimeout(r, timeout))]);
      }
    } catch (e) {
      // ignore
    }
  }

  // Cleanup any leftover temp nodes and object URLs
  function cleanup() {
    try {
      document.querySelectorAll('iframe[data-pf-temp="1"]').forEach(n => n.remove());
      document.querySelectorAll('div[data-pf-temp="1"]').forEach(n => n.remove());
      if (window.__pf_lastObjectURL) {
        try { URL.revokeObjectURL(window.__pf_lastObjectURL); } catch (e) {}
        window.__pf_lastObjectURL = null;
      }
    } catch (e) {
      console.warn('cleanup error', e);
    }
  }

  /* ---------- Init function ---------- */
  function initProformaInvoice() {
    console.log('proforma-invoice.js init');

    try { // <- wrap entire init in try/catch so errors are visible
      const ALLOWED_CODES = ['TEAMRAW', 'RAW2025', 'RAWADMIN'];
      const SESSION_KEY = 'pf_internal_ok';

      // Supported currencies: code -> { symbol, fallback rate from INR }
      // Fallback rates as of Feb 19, 2026 — only used if live API fetch fails
      const CURRENCIES = {
        INR: { symbol: '₹',   fallback: 1        },
        USD: { symbol: '$',   fallback: 0.01106  },
        EUR: { symbol: '€',   fallback: 0.00936  },
        GBP: { symbol: '£',   fallback: 0.00807  },
        AED: { symbol: 'AED', fallback: 0.04062  },
        SGD: { symbol: 'S$',  fallback: 0.01407  },
        AUD: { symbol: 'A$',  fallback: 0.01574  },
        CAD: { symbol: 'C$',  fallback: 0.01510  },
        SAR: { symbol: 'SAR', fallback: 0.04148  },
        MYR: { symbol: 'RM',  fallback: 0.04348  },
        // Extended currencies
        IDR: { symbol: 'Rp',  fallback: 170.5    },
        THB: { symbol: '฿',   fallback: 0.3706   },
        PHP: { symbol: '₱',   fallback: 0.6312   },
        BDT: { symbol: '৳',   fallback: 1.2100   },
        PKR: { symbol: '₨',   fallback: 3.0780   },
        LKR: { symbol: 'Rs',  fallback: 3.3150   },
        NPR: { symbol: 'रू',  fallback: 1.7700   },
        QAR: { symbol: 'QAR', fallback: 0.04026  },
        KWD: { symbol: 'KD',  fallback: 0.003400 },
        BHD: { symbol: 'BD',  fallback: 0.004168 },
        OMR: { symbol: 'OMR', fallback: 0.004258 },
        JOD: { symbol: 'JD',  fallback: 0.007843 },
        EGP: { symbol: 'E£',  fallback: 0.5426   },
        ZAR: { symbol: 'R',   fallback: 0.2048   },
        NGN: { symbol: '₦',   fallback: 17.23    },
        KES: { symbol: 'KSh', fallback: 1.4260   },
        GHS: { symbol: 'GH₵', fallback: 0.1654   },
        TZS: { symbol: 'TSh', fallback: 28.97    },
        MXN: { symbol: 'MX$', fallback: 0.2158   },
        BRL: { symbol: 'R$',  fallback: 0.06309  },
        CLP: { symbol: 'CL$', fallback: 10.65    },
        COP: { symbol: 'CO$', fallback: 44.05    },
        PEN: { symbol: 'S/',  fallback: 0.04120  },
        CHF: { symbol: 'Fr',  fallback: 0.009730 },
        SEK: { symbol: 'kr',  fallback: 0.1099   },
        NOK: { symbol: 'kr',  fallback: 0.1175   },
        DKK: { symbol: 'kr',  fallback: 0.06982  },
        PLN: { symbol: 'zł',  fallback: 0.04362  },
        CZK: { symbol: 'Kč',  fallback: 0.2499   },
        HUF: { symbol: 'Ft',  fallback: 4.0310   },
        RON: { symbol: 'lei', fallback: 0.04668  },
        TRY: { symbol: '₺',   fallback: 0.3810   },
        RUB: { symbol: '₽',   fallback: 1.0200   },
        UAH: { symbol: '₴',   fallback: 0.4597   },
        NZD: { symbol: 'NZ$', fallback: 0.01724  },
        HKD: { symbol: 'HK$', fallback: 0.08628  },
        TWD: { symbol: 'NT$', fallback: 0.3625   },
        KRW: { symbol: '₩',   fallback: 15.47    },
        JPY: { symbol: '¥',   fallback: 1.6610   },
        CNY: { symbol: '¥',   fallback: 0.08047  },
        VND: { symbol: '₫',   fallback: 272.8    },
        MMK: { symbol: 'K',   fallback: 23.20    },
        KHR: { symbol: '៛',   fallback: 44.57    },
        ILS: { symbol: '₪',   fallback: 0.04045  },
        MAD: { symbol: 'MAD', fallback: 0.1107   },
        DZD: { symbol: 'DA',  fallback: 1.4960   },
        TND: { symbol: 'DT',  fallback: 0.03424  },
        ETB: { symbol: 'Br',  fallback: 1.3230   },
        UGX: { symbol: 'USh', fallback: 40.75    },
        XOF: { symbol: 'CFA', fallback: 6.141    },
        XAF: { symbol: 'CFA', fallback: 6.141    },
        ARS: { symbol: 'AR$', fallback: 12.55    },
      };
      const CURRENCY_SYMBOLS = Object.fromEntries(
        Object.entries(CURRENCIES).map(([k, v]) => [k, v.symbol])
      );

      // Country list with associated currency
      const COUNTRY_CURRENCY_MAP = {
        // South Asia
        'India':               'INR',
        'Pakistan':            'PKR',
        'Bangladesh':          'BDT',
        'Sri Lanka':           'LKR',
        'Nepal':               'NPR',
        // Middle East
        'UAE':                 'AED',
        'Saudi Arabia':        'SAR',
        'Qatar':               'QAR',
        'Kuwait':              'KWD',
        'Bahrain':             'BHD',
        'Oman':                'OMR',
        'Jordan':              'JOD',
        'Egypt':               'EGP',
        'Israel':              'ILS',
        // Southeast Asia
        'Singapore':           'SGD',
        'Malaysia':            'MYR',
        'Indonesia':           'IDR',
        'Thailand':            'THB',
        'Philippines':         'PHP',
        'Vietnam':             'VND',
        'Myanmar':             'MMK',
        'Cambodia':            'KHR',
        // East Asia
        'Japan':               'JPY',
        'China':               'CNY',
        'Hong Kong':           'HKD',
        'Taiwan':              'TWD',
        'South Korea':         'KRW',
        // Oceania
        'Australia':           'AUD',
        'New Zealand':         'NZD',
        // North America
        'United States':       'USD',
        'Canada':              'CAD',
        'Mexico':              'MXN',
        // Europe
        'United Kingdom':      'GBP',
        'Germany':             'EUR',
        'France':              'EUR',
        'Italy':               'EUR',
        'Spain':               'EUR',
        'Netherlands':         'EUR',
        'Belgium':             'EUR',
        'Portugal':            'EUR',
        'Austria':             'EUR',
        'Greece':              'EUR',
        'Ireland':             'EUR',
        'Finland':             'EUR',
        'Switzerland':         'CHF',
        'Sweden':              'SEK',
        'Norway':              'NOK',
        'Denmark':             'DKK',
        'Poland':              'PLN',
        'Czech Republic':      'CZK',
        'Hungary':             'HUF',
        'Romania':             'RON',
        'Turkey':              'TRY',
        'Russia':              'RUB',
        'Ukraine':             'UAH',
        // Africa
        'South Africa':        'ZAR',
        'Nigeria':             'NGN',
        'Kenya':               'KES',
        'Ghana':               'GHS',
        'Tanzania':            'TZS',
        'Ethiopia':            'ETB',
        'Uganda':              'UGX',
        'Morocco':             'MAD',
        'Algeria':             'DZD',
        'Tunisia':             'TND',
        'Senegal':             'XOF',
        'Ivory Coast':         'XOF',
        'Cameroon':            'XAF',
        // South America
        'Brazil':              'BRL',
        'Argentina':           'ARS',
        'Chile':               'CLP',
        'Colombia':            'COP',
        'Peru':                'PEN',
        // Fallback
        'Other':               'USD',
      };

      // Live rates (INR base), fetched from open.er-api.com
      // open.er-api.com supports AED, SAR, MYR, SGD and all currencies — no API key required
      let _liveRates = {};
      let _rateLastFetchedAt = 0;

      async function fetchLiveRates() {
        const CACHE_MS = 60 * 60 * 1000; // cache 1 hour
        const now = Date.now();
        if (now - _rateLastFetchedAt < CACHE_MS && Object.keys(_liveRates).length > 0) return;
        try {
          const res = await fetch('https://open.er-api.com/v6/latest/INR', { cache: 'no-store' });
          if (!res.ok) throw new Error('HTTP ' + res.status);
          const data = await res.json();
          if (data && data.result === 'success' && data.rates) {
            // Store only the currencies we use
            const needed = Object.keys(CURRENCIES).filter(c => c !== 'INR');
            needed.forEach(c => {
              if (data.rates[c] && typeof data.rates[c] === 'number') {
                _liveRates[c] = data.rates[c];
              }
            });
            _rateLastFetchedAt = now;
            console.log('Live rates fetched (INR base):', _liveRates);
            renderItemsTable();
            updateGrandTotalDisplay();
          } else {
            throw new Error('Unexpected API response format');
          }
        } catch (err) {
          console.warn('Could not fetch live rates, using fallbacks.', err);
        }
      }

      // Alias for backward compat with applyAuthState call
      const fetchLiveUsdRate = fetchLiveRates;

      function getCurrencyRate(currency) {
        if (currency === 'INR') return 1;
        const live = _liveRates[currency];
        if (live && typeof live === 'number' && live > 0) return live;
        return CURRENCIES[currency] ? CURRENCIES[currency].fallback : 1;
      }

      function formatCurrency(n, currency) {
        const cur = currency || currentCurrency;
        const num = Number(n) || 0;
        const rate = getCurrencyRate(cur);
        const symbol = CURRENCY_SYMBOLS[cur] || '';
        return `${symbol} ${(num * rate).toFixed(2)}`;
      }

      const authBlock       = $('pf-auth-block');
      const codeInput       = $('pf-code');
      const authBtn         = $('pf-auth-btn');
      const logoutBtn       = $('pf-logout-btn');
      const logoutWrap      = $('pf-logout-wrap');
      const authMsg         = $('pf-auth-msg');
      const customerBlock   = $('pf-customer-block');
      const controlsBlock   = $('pf-controls');
      const uiBlock         = $('pf-ui-block');
      const importBtn       = $('pf-import-cart');
      const addTitle        = $('pf_item_title');
      const addGrade        = $('pf_item_grade');
      const addQty          = $('pf_item_qty');
      const addPrice        = $('pf_item_price');
      const addBtn          = $('pf-item-add');
      const createBtn       = $('pf-create-btn');
      const previewBtn      = $('pf-preview-btn');
      const statusEl        = $('pf-status');
      const forceInrCheck   = $('pf-force-inr');
      const currencyBadge   = $('pf-currency-badge');

      const discountAmtInput   = $('pf_discount_amount');
      const discountPctInput   = $('pf_discount_percent');
      const proformaNoInput    = $('pf_proforma_number');
      const proformaDateInput  = $('pf_proforma_date');
      const bankDetailsInput   = $('pf-bank-details');
      const paymentTermsInput  = $('pf-payment-terms');

      const rootEl    = $('proforma-root');
      const LOGO_URL  = rootEl ? (rootEl.getAttribute('data-logo-url') || '') : '';
      const COMPANY_NAME    = rootEl ? (rootEl.getAttribute('data-company-name')    || 'Raw Aroma Chem') : 'Raw Aroma Chem';
      const COMPANY_ADDRESS = rootEl ? (rootEl.getAttribute('data-company-address') || '2, Shrinagar Main, Indore, Madhya Pradesh 452001, India') : '';
      const COMPANY_PHONE   = rootEl ? (rootEl.getAttribute('data-company-phone')   || '+91 8435083902') : '';
      const COMPANY_EMAIL   = rootEl ? (rootEl.getAttribute('data-company-email')   || 'rawaromach@gmail.com') : '';
      const COMPANY_GSTIN   = rootEl ? (rootEl.getAttribute('data-company-gstin')   || '23AADCC7913N1ZI') : '';
      const DEFAULT_PAYMENT_TERMS = rootEl ? (rootEl.getAttribute('data-default-payment-terms') || '') : '';
      const DEFAULT_BANK_DETAILS  = rootEl ? (rootEl.getAttribute('data-default-bank-details')  || '') : '';

      // Pre-fill payment terms and bank details from schema defaults
      if (bankDetailsInput && DEFAULT_BANK_DETAILS && !bankDetailsInput.value) {
        bankDetailsInput.value = DEFAULT_BANK_DETAILS;
      }
      if (paymentTermsInput && DEFAULT_PAYMENT_TERMS && !paymentTermsInput.value) {
        paymentTermsInput.value = DEFAULT_PAYMENT_TERMS;
      }

      // Ensure common buttons are not accidentally submit buttons
      ['pf-auth-btn','pf-create-btn','pf-preview-btn','pf-item-add','pf-import-cart'].forEach(id => {
        const el = document.getElementById(id);
        if (el && el.tagName === 'BUTTON' && !el.hasAttribute('type')) el.setAttribute('type','button');
      });

      if (!createBtn || !authBtn || !codeInput) {
        console.warn('Proforma: missing critical elements; aborting.');
        return;
      }

      let items = [];
      let currentCurrency = 'INR';
      let discountAmount = 0;
      let _currencyManuallySet = false; // true once user explicitly picks a currency

      // Pre-fetch live rates as soon as the script initialises
      fetchLiveRates();

      // Inject country dropdown + currency selector into the DOM if placeholder exists
      (function injectCountrySelector() {
        const placeholder = $('pf-country-currency-block');
        if (!placeholder) return;
        const countryOptions = Object.keys(COUNTRY_CURRENCY_MAP)
          .map(c => `<option value="${escapeHtml(c)}">${escapeHtml(c)}</option>`)
          .join('');
        const currencyOptions = Object.keys(CURRENCIES)
          .map(c => `<option value="${c}">${c} (${CURRENCIES[c].symbol})</option>`)
          .join('');
        placeholder.innerHTML = `
          <div style="display:flex;gap:12px;flex-wrap:wrap;align-items:flex-end;margin-bottom:10px;">
            <div>
              <label style="font-size:12px;font-weight:600;display:block;margin-bottom:4px;">Country</label>
              <select id="pf_customer_country" style="padding:8px 10px;border:1px solid #ddd;border-radius:6px;font-size:13px;min-width:180px;">
                <option value="">— Select Country —</option>
                ${countryOptions}
              </select>
            </div>
            <div>
              <label style="font-size:12px;font-weight:600;display:block;margin-bottom:4px;">Invoice Currency</label>
              <select id="pf_invoice_currency" style="padding:8px 10px;border:1px solid #ddd;border-radius:6px;font-size:13px;min-width:170px;">
                ${currencyOptions}
              </select>
            </div>
          </div>
        `;
      })();

      function isIndianCustomer() {
        const countryEl = $('pf_customer_country');
        // If no country selected yet, default to India (conservative — shows GST fields)
        if (!countryEl || !countryEl.value) return true;
        return countryEl.value === 'India';
      }

      function updateGstVisibility() {
        const gstBlock = $('pf-gst-block');
        if (!gstBlock) return;
        const isIndia = isIndianCustomer();
        gstBlock.style.display = isIndia ? '' : 'none';
        if (!isIndia) {
          // Force GST off for international customers
          const noneRadio = $('pf-gst-none');
          if (noneRadio) noneRadio.checked = true;
          items.forEach(item => { item.gst_percent = 0; });
        } else {
          // Restore default 18% for Indian items that were zeroed
          items.forEach(item => {
            if (item.gst_percent === 0) item.gst_percent = 18;
          });
        }
      }

      function updateCurrency(triggeredBy) {
        const countryEl = $('pf_customer_country');
        const currencyEl = $('pf_invoice_currency');

        // Auto-set currency when country changes — but only if user hasn't manually picked a currency
        if (triggeredBy === 'country' && countryEl && countryEl.value && currencyEl) {
          const mapped = COUNTRY_CURRENCY_MAP[countryEl.value];
          if (mapped) {
            currencyEl.value = mapped;
            _currencyManuallySet = false; // country change resets the manual flag
          }
        }

        const newCurr = (currencyEl && currencyEl.value) ? currencyEl.value : 'INR';
        const changed = newCurr !== currentCurrency;
        currentCurrency = newCurr;

        if (currencyBadge) {
          const sym = CURRENCY_SYMBOLS[currentCurrency] || '';
          currencyBadge.textContent = currentCurrency + (sym ? ' (' + sym + ')' : '');
          show($('pf-currency-hint'));
        }

        updateGstVisibility();
        if (changed) renderItemsTable();
        updateGrandTotalDisplay();
      }

      // Wire up dropdowns (use setTimeout so injected DOM is ready)
      setTimeout(() => {
        const countryEl2 = $('pf_customer_country');
        const currencyEl2 = $('pf_invoice_currency');
        if (countryEl2) countryEl2.addEventListener('change', () => updateCurrency('country'));
        if (currencyEl2) currencyEl2.addEventListener('change', () => {
          _currencyManuallySet = true;
          updateCurrency('currency');
        });
      }, 0);
      if (forceInrCheck) forceInrCheck.addEventListener('change', () => {
        if (forceInrCheck.checked) {
          const currencyEl3 = $('pf_invoice_currency');
          if (currencyEl3) currencyEl3.value = 'INR';
          _currencyManuallySet = false;
          currentCurrency = 'INR';
          if (currencyBadge) {
            currencyBadge.textContent = 'INR (₹) — Forced';
            show($('pf-currency-hint'));
          }
          renderItemsTable();
          updateGrandTotalDisplay();
        } else {
          _currencyManuallySet = false;
          updateCurrency('country');
        }
      });


      // GST type radio buttons → affects display
      document.querySelectorAll('input[name="pf-gst-type"]').forEach(radio => {
        radio.addEventListener('change', () => {
          renderItemsTable();
        });
      });

      // Discount amount input → affects grand total
      if (discountAmtInput) {
        discountAmtInput.addEventListener('input', () => {
          const raw = parseFloat(discountAmtInput.value) || 0;
          if (raw < 0) discountAmtInput.value = 0;
          discountAmount = Math.max(0, raw);
          updateGrandTotalDisplay();
        });
      }

      function isAuthed()    { return sessionStorage.getItem(SESSION_KEY) === '1'; }
      function setAuthed()   { sessionStorage.setItem(SESSION_KEY, '1'); }
      function clearAuth()   { sessionStorage.removeItem(SESSION_KEY); }

      function applyAuthState() {
        if (isAuthed()) {
          hide(authBlock);
          show(logoutWrap);
          show(customerBlock);
          show(controlsBlock);
          show(uiBlock);
          show(previewBtn);
          fetchLiveRates(); // fetch / refresh live rate whenever UI becomes active
          // Small delay so injected country/currency DOM is ready before updateCurrency
          setTimeout(updateCurrency, 0);
        } else {
          show(authBlock);
          hide(logoutWrap);
          hide(customerBlock);
          hide(controlsBlock);
          hide(uiBlock);
          hide(previewBtn);
        }
      }

      function validateCustomer() {
        const n   = $('pf_customer_name')?.value.trim();
        const a   = $('pf_customer_address')?.value.trim();
        const p   = $('pf_customer_phone')?.value.trim();
        const e   = $('pf_customer_email')?.value.trim();
        const err = $('pf_customer_error');

        if (err) err.textContent = '';

        if (!n) { if (err) err.textContent = 'Contact Name required.'; return false; }
        if (!a) { if (err) err.textContent = 'Full Address required.'; return false; }
        if (!p) { if (err) err.textContent = 'Phone required.'; return false; }
        if (e && !/^\S+@\S+\.\S+$/.test(e)) {
          if (err) err.textContent = 'Please enter a valid email address.';
          return false;
        }
        return true;
      }

      function validateItems() {
        const err = $('pf-notes-error');
        if (err) err.textContent = '';
        if (!items.length) {
          if (err) err.textContent = 'Please add at least one item to the Proforma Invoice.';
          return false;
        }
        return true;
      }

      function getSelectedGstType() {
        const igst = $('pf-gst-igst');
        const none = $('pf-gst-none');
        if (none && none.checked) return 'none';
        if (igst && igst.checked) return 'igst';
        return 'cgst_sgst'; // default
      }

      function calculateGstTotals() {
        let totalGst = 0;
        let subtotal = 0;
        items.forEach(i => {
          const lineTotal = i.qty * i.unit_price; // unit_price is GST-inclusive
          const gstPercent = i.gst_percent || 0;
          // Reverse-extract: GST = lineTotal - (lineTotal × 100 / (100 + gst%))
          const gstAmount = gstPercent > 0
            ? lineTotal - (lineTotal * 100) / (100 + gstPercent)
            : 0;
          subtotal += lineTotal - gstAmount; // base price before GST
          totalGst += gstAmount;
        });
        return { subtotal, totalGst };
      }

      function updateGrandTotalDisplay() {
        const { subtotal, totalGst } = calculateGstTotals();
        const gstType = getSelectedGstType();
        // subtotal + totalGst = the original GST-inclusive total (no double-counting)
        const inclusiveTotal = subtotal + totalGst;
        const grandTotal = Math.max(0, inclusiveTotal - discountAmount);

        if (statusEl) {
          let statusText = '';
          if (gstType === 'none') {
            statusText = `Total: ${formatCurrency(inclusiveTotal, currentCurrency)}`;
          } else if (gstType === 'igst') {
            statusText = `Base: ${formatCurrency(subtotal, currentCurrency)} | IGST (incl.): ${formatCurrency(totalGst, currentCurrency)}`;
          } else {
            const halfGst = totalGst / 2;
            statusText = `Base: ${formatCurrency(subtotal, currentCurrency)} | CGST (incl.): ${formatCurrency(halfGst, currentCurrency)} | SGST (incl.): ${formatCurrency(halfGst, currentCurrency)}`;
          }
          
          if (discountAmount > 0) {
            statusText += ` | Discount: -${formatCurrency(discountAmount, currentCurrency)}`;
          }
          statusText += ` | Grand Total: ${formatCurrency(grandTotal, currentCurrency)}`;
          statusEl.textContent = statusText;
        }

        const countEl = $('pf-item-count');
        if (countEl) {
          countEl.textContent = `${items.length} Item${items.length !== 1 ? 's' : ''}`;
        }
      }

      function updateGstColumns() {
        // Show/hide GST % and GST Amt header <th> columns to match row cells
        const table = $('pf-items-table');
        if (!table) return;
        const showGst = isIndianCustomer();
        const headers = table.querySelectorAll('thead th');
        // indices: 0=Item 1=Grade 2=Qty 3=UnitPrice 4=GST% 5=GSTAmt 6=LineTotal 7=(delete)
        if (headers.length >= 7) {
          headers[4].style.display = showGst ? '' : 'none';
          headers[5].style.display = showGst ? '' : 'none';
        }
      }

      function renderItemsTable() {
        const tbody = $('pf-items-tbody');
        if (!tbody) return;

        updateGstColumns();

        if (!items.length) {
        tbody.innerHTML =
            `<tr><td colspan="${isIndianCustomer() ? '8' : '6'}" style="padding:12px;text-align:center;color:#666">No items added</td></tr>`;
          updateGrandTotalDisplay();
          return;
        }

        tbody.innerHTML = items.map((it, idx) => {
          const lineTotal = it.qty * it.unit_price; // unit_price is GST-inclusive
          const gstPercent = it.gst_percent || 0;
          const showGst = isIndianCustomer();
          // Reverse-extract GST from the inclusive price
          const gstAmount = (showGst && gstPercent > 0)
            ? lineTotal - (lineTotal * 100) / (100 + gstPercent)
            : 0;
          
          return `
          <tr data-idx="${idx}">
            <td style="padding:6px;border-bottom:1px solid #f6f6f6">
              <input
                class="pf-title"
                data-idx="${idx}"
                value="${escapeHtml(it.title || '')}"
                style="padding:6px;border:1px solid #ddd;border-radius:4px;width:100%;min-width:140px;box-sizing:border-box"
              />
            </td>
            <td style="padding:6px;border-bottom:1px solid #f6f6f6">
              <input
                class="pf-grade"
                data-idx="${idx}"
                value="${escapeHtml(it.grade || '')}"
                style="padding:6px;border:1px solid #ddd;border-radius:4px;width:100%;box-sizing:border-box"
              />
            </td>
            <td style="padding:6px;text-align:center;border-bottom:1px solid #f6f6f6">
              <input
                class="pf-qty"
                type="number"
                min="1"
                data-idx="${idx}"
                value="${it.qty}"
                style="width:60px;padding:6px;border:1px solid #ddd;border-radius:4px;text-align:center"
              />
            </td>
            <td style="padding:6px;text-align:right;border-bottom:1px solid #f6f6f6">
              <input
                class="pf-unit-price"
                type="number"
                min="0"
                step="0.01"
                data-idx="${idx}"
                value="${it.unit_price.toFixed(2)}"
                style="width:90px;padding:6px;border:1px solid #ddd;border-radius:4px;text-align:right"
              />
            </td>
            ${showGst ? `
            <td style="padding:6px;text-align:center;border-bottom:1px solid #f6f6f6">
              <input
                class="pf-gst-percent"
                type="number"
                min="0"
                max="100"
                step="0.01"
                data-idx="${idx}"
                value="${gstPercent}"
                style="width:55px;padding:6px;border:1px solid #ddd;border-radius:4px;text-align:center"
              />
            </td>
            <td class="pf-gst-amount" style="padding:6px;text-align:right;border-bottom:1px solid #f6f6f6;color:#1565c0;font-weight:500;">
              ${formatCurrency(gstAmount, currentCurrency)}
            </td>
            ` : ''}
            <td class="pf-line-total" style="padding:6px;text-align:right;border-bottom:1px solid #f6f6f6;font-weight:600;">
              ${formatCurrency(lineTotal, currentCurrency)}
            </td>
            <td style="padding:6px;text-align:center;border-bottom:1px solid #f6f6f6">
              <button
                class="pf-remove"
                data-idx="${idx}"
                style="background:#ff4d4f;color:#fff;border:none;padding:5px 8px;border-radius:4px;cursor:pointer;font-size:12px;"
              >
                ✕
              </button>
            </td>
          </tr>
        `}).join('');

        tbody.querySelectorAll('.pf-remove').forEach(b => {
          b.onclick = e => {
            const idx = Number(e.target.dataset.idx);
            if (!Number.isNaN(idx)) {
              items.splice(idx, 1);
              renderItemsTable();
            }
          };
        });

        tbody.querySelectorAll('.pf-qty, .pf-unit-price, .pf-grade, .pf-title, .pf-gst-percent').forEach(el => {
          const isTextField = el.classList.contains('pf-grade') || el.classList.contains('pf-title');
          const isNumberField = el.classList.contains('pf-qty') || el.classList.contains('pf-unit-price') || el.classList.contains('pf-gst-percent');
          const updateItem = () => {
            const idx = Number(el.dataset.idx);
            if (Number.isNaN(idx) || !items[idx]) return;

            if (el.classList.contains('pf-qty')) {
              items[idx].qty = Math.max(1, Math.floor(Number(el.value) || 1));
            } else if (el.classList.contains('pf-unit-price')) {
              items[idx].unit_price = Math.max(0, parseFloat(el.value) || 0);
            } else if (el.classList.contains('pf-gst-percent')) {
              items[idx].gst_percent = Math.max(0, Math.min(100, parseFloat(el.value) || 0));
            } else if (el.classList.contains('pf-title')) {
              items[idx].title = el.value;
            } else {
              items[idx].grade = el.value;
            }
            renderItemsTable();
          };
          // Number inputs: use onchange (fires on blur/Enter) to avoid re-render mid-typing
          // Text inputs: use onblur so cursor is never disrupted
          if (isNumberField) el.onchange = updateItem;
          else el.onblur = updateItem;
        });

        updateGrandTotalDisplay();
      }


      // Add item
      if (addBtn) {
        addBtn.onclick = () => {
          const title = addTitle?.value.trim() || '';
          const grade = addGrade?.value.trim() || '';
          const qty   = Math.max(1, Math.floor(Number(addQty?.value) || 1));
          const price = Math.max(0, parseFloat(addPrice?.value) || 0);

          if (!title) return alert('Enter item title');
          if (price <= 0) return alert('Enter a valid unit price greater than 0');

          // Use 18% GST for Indian customers, 0% for international
          const gstDefault = isIndianCustomer() ? 18 : 0;
          items.push({ title, grade, qty, unit_price: price, gst_percent: gstDefault });
          renderItemsTable();

          if (addTitle) addTitle.value = '';
          if (addGrade) addGrade.value = '';
          if (addQty)   addQty.value   = 1;
          if (addPrice) addPrice.value = '';
        };
      }

      // Import from Shopify cart
      if (importBtn) {
        importBtn.onclick = async () => {
          if (!isAuthed()) return alert('Enter code first');
          const original = importBtn.textContent;
          importBtn.disabled = true;
          importBtn.textContent = 'Importing...';
          try {
            const r = await fetch('/cart.js');
            if (!r.ok) throw new Error('Failed to fetch cart');
            const cart = await r.json();
            if (!cart.items?.length) {
              alert('Cart is empty.');
            } else {
              const gstDefault = isIndianCustomer() ? 18 : 0;
              cart.items.forEach(i => items.push({
                title: i.product_title + (i.variant_title ? ' — ' + i.variant_title : ''),
                grade: '',
                qty: i.quantity,
                unit_price: (i.price || 0) / 100,
                gst_percent: gstDefault
              }));
              renderItemsTable();
              alert(`Successfully imported ${cart.items.length} items.`);
            }
          } catch (err) {
            console.error('Cart import failed', err);
            alert('Failed to import items from Cart. Check console.');
          } finally {
            importBtn.disabled = false;
            importBtn.textContent = original;
          }
        };
      }

      // Build printable HTML
      function buildPrintableNode(customer, itemsList, notes, currency, options = {}) {
        const esc = s => escapeHtml(String(s || ''));

        const {
          proformaNumber      = '',
          proformaDate        = '',
          bankDetails         = '',
          paymentTerms        = '',
          discountAmountOpt   = 0,
          discountPercentNote = '',
          gstType             = 'cgst_sgst' // 'cgst_sgst', 'igst', or 'none'
        } = options || {};

        // GST is INCLUSIVE in unit_price — reverse-extract base and GST
        const totalGst = gstType !== 'none'
          ? itemsList.reduce((sum, item) => {
              const lineTotal = item.qty * item.unit_price;
              const gstPercent = item.gst_percent || 0;
              return sum + (gstPercent > 0 ? lineTotal - (lineTotal * 100) / (100 + gstPercent) : 0);
            }, 0)
          : 0;

        const subtotal = itemsList.reduce((sum, item) => {
          const lineTotal = item.qty * item.unit_price;
          const gstPercent = (gstType !== 'none' && item.gst_percent) ? item.gst_percent : 0;
          const gstAmount = gstPercent > 0 ? lineTotal - (lineTotal * 100) / (100 + gstPercent) : 0;
          return sum + lineTotal - gstAmount; // base price (ex-GST)
        }, 0);

        const discount   = Math.max(0, discountAmountOpt || 0);
        // Grand total = subtotal + totalGst (= original inclusive sum) - discount
        const grandTotal = Math.max(0, (subtotal + totalGst) - discount);
        const currencySymbol = CURRENCY_SYMBOLS[currency] || '';

        let formattedDate = '';
        if (proformaDate) {
          try {
            const d = new Date(proformaDate);
            if (!isNaN(d.getTime())) {
              const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
              formattedDate = `${String(d.getDate()).padStart(2,'0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
            }
          } catch (e) {}
        }

        // Build GST rows for tfoot
        let gstRows = '';
        if (gstType === 'igst' && totalGst > 0) {
          gstRows = `
            <tr>
              <td colspan="5" class="right" style="padding-right:10px;">IGST:</td>
              <td class="right">${formatCurrency(totalGst, currency)}</td>
            </tr>
          `;
        } else if (gstType === 'cgst_sgst' && totalGst > 0) {
          const halfGst = totalGst / 2;
          gstRows = `
            <tr>
              <td colspan="5" class="right" style="padding-right:10px;">CGST:</td>
              <td class="right">${formatCurrency(halfGst, currency)}</td>
            </tr>
            <tr>
              <td colspan="5" class="right" style="padding-right:10px;">SGST:</td>
              <td class="right">${formatCurrency(halfGst, currency)}</td>
            </tr>
          `;
        }

        return `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<style>
  html,body{margin:0;padding:0;background:#fff;color:#111;}
  body{
    font-family:Arial,Helvetica,sans-serif;
    font-size:11.5px;
    line-height:1.35;
    color:#222;
    padding:8mm 10mm 8mm 10mm;
    box-sizing:border-box;
  }
  .header{
    display:flex;
    justify-content:space-between;
    align-items:flex-start;
    margin-bottom:6px;
  }
  .title{
    font-size:18px;
    font-weight:700;
    color:#0b2b4a;
    text-align:center;
  }
  .subtitle{
    font-size:10.5px;
    color:#6b7280;
    text-align:center;
    margin-top:4px;
  }
  .logo-block img{
    max-height:38px;
    width:auto;
    display:block;
    margin-bottom:6px;
  }
  table{
    width:100%;
    border-collapse:collapse;
    margin-top:8px;
    font-size:11px;
  }
  thead{display:table-header-group}
  tfoot{display:table-footer-group}
  thead th{
    background:#f5f7fa;
    padding:8px;
    border:1px solid #e6e9ee;
    font-weight:700;
    text-align:left;
  }
  tbody td{
    padding:8px;
    border:1px solid #eee;
    vertical-align:top;
  }
  tbody tr{page-break-inside:avoid;}
  .right{text-align:right}
  .center{text-align:center}
  .notes{
    margin-top:10px;
    padding:8px;
    background:#fbfbfb;
    border-left:3px solid #e9eef6;
    border-radius:3px;
  }
  .terms{
    margin-top:10px;
    font-size:10px;
    color:#666;
  }
  .bank-block{
    margin-top:8px;
    padding:8px;
    border:1px solid #e0e4f0;
    border-radius:3px;
    font-size:10.5px;
    background:#fafbff;
  }
  .meta-row{
    font-size:10.5px;
    color:#555;
    margin-top:4px;
    text-align:right;
  }
  .gst-row td{
    background:#f8faff;
  }
  .total-row td{
  border-top:2px solid #333;
  border-bottom:2px solid #333;
  font-weight:700;
  font-size:12px;
  padding:10px 8px;
}
</style>
</head>
<body>
  <div class="header">
    <div style="flex:1" class="logo-block">
      ${LOGO_URL ? `<img src="${esc(LOGO_URL)}" alt="Logo">` : ''}
      <strong>${esc(COMPANY_NAME)}</strong><br/>
      ${esc(COMPANY_ADDRESS).replace(/,\s*/g, ',<br/>')}<br/>
      ${COMPANY_PHONE ? `Phone: ${esc(COMPANY_PHONE)}<br/>` : ''}
      ${COMPANY_EMAIL ? `Email: ${esc(COMPANY_EMAIL)}<br/>` : ''}
      ${COMPANY_GSTIN ? `GSTIN: ${esc(COMPANY_GSTIN)}` : ''}
    </div>
    <div style="flex:1">
      <div class="title">PROFORMA INVOICE</div>
      <div class="subtitle">Estimate only — Prices in <strong>${currencySymbol} ${esc(currency)}</strong></div>
      <div class="meta-row">
        ${proformaNumber ? `Proforma No.: <strong>${esc(proformaNumber)}</strong><br/>` : ''}
        ${formattedDate ? `Date: <strong>${esc(formattedDate)}</strong>` : ''}
      </div>
    </div>
    <div style="flex:1;text-align:right">
      <strong>To:</strong><br/>
      <strong>${esc(customer.name)}</strong> ${customer.company ? `(${esc(customer.company)})` : ''}<br/>
      ${esc(customer.address).replace(/\n/g,'<br/>')}${customer.country ? `<br/><strong>${esc(customer.country)}</strong>` : ''}<br/>
      Phone: ${esc(customer.phone)}<br/>
      ${customer.email ? `Email: ${esc(customer.email)}<br/>` : ''}${customer.gst ? `${customer.country === 'India' ? 'GSTIN' : 'VAT/Tax ID'}: ${esc(customer.gst)}` : ''}
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th style="width:${gstType !== 'none' ? '36' : '42'}%;">Item Description</th>
        <th style="width:${gstType !== 'none' ? '14' : '16'}%;">Grade</th>
        <th style="width:8%" class="center">Qty</th>
        <th style="width:${gstType !== 'none' ? '14' : '18'}%" class="right">Unit Price (${currencySymbol})</th>
        ${gstType !== 'none' ? `<th style="width:13%" class="right">GST Amt (${currencySymbol})</th>` : ''}
        <th style="width:${gstType !== 'none' ? '15' : '16'}%" class="right">Amount (${currencySymbol})</th>
      </tr>
    </thead>
    <tbody>
      ${itemsList.map(i => {
        const lineTotal = i.qty * i.unit_price; // GST-inclusive
        const gstPercent = i.gst_percent || 0;
        const gstAmount = (gstType !== 'none' && gstPercent > 0)
          ? lineTotal - (lineTotal * 100) / (100 + gstPercent)
          : 0;
        const baseUnitPrice = gstAmount > 0 ? (i.unit_price * 100) / (100 + gstPercent) : i.unit_price;
        return `
        <tr>
          <td>${esc(i.title)}</td>
          <td>${esc(i.grade)}</td>
          <td class="center">${i.qty}</td>
          <td class="right">${formatCurrency(baseUnitPrice, currency)}</td>
          ${gstType !== 'none' ? `<td class="right">${formatCurrency(gstAmount, currency)}</td>` : ''}
          <td class="right">${formatCurrency(lineTotal, currency)}</td>
        </tr>
      `}).join('')}
    </tbody>
    <tfoot>
      <tr>
        <td colspan="${gstType !== 'none' ? '5' : '4'}" class="right" style="padding-right:10px;">Subtotal:</td>
        <td class="right">${formatCurrency(subtotal, currency)}</td>
      </tr>
      ${gstRows}
      ${discount > 0 ? `
      <tr>
        <td colspan="${gstType !== 'none' ? '5' : '4'}" class="right" style="padding-right:10px;">
          Discount${discountPercentNote ? ` (${esc(discountPercentNote)}%)` : ''}:
        </td>
        <td class="right">- ${formatCurrency(discount, currency)}</td>
      </tr>
      ` : ''}
      <tr class="total-row">
        <td colspan="${gstType !== 'none' ? '5' : '4'}" class="right" style="padding-right:10px;">GRAND TOTAL:</td>
        <td class="right">${formatCurrency(grandTotal, currency)}</td>
      </tr>
    </tfoot>
  </table>

  ${notes ? `
    <div class="notes">
      <strong>Notes:</strong>
      <div style="margin-top:6px;">${esc(notes).replace(/\n/g,'<br/>')}</div>
    </div>
  ` : ''}

  <div class="terms">
    <div style="font-weight:700;margin-top:8px;">Terms & Conditions:</div>
    <ul style="margin:6px 0 0 18px;color:#666;">
      <li>This is a proforma invoice and not a tax invoice.</li>
      <li>Prices valid for 15 days from date of issue.</li>
      <li>Subject to availability at time of order confirmation.</li>
      ${gstType === 'none' && customer.country === 'India' ? '<li>GST extra as applicable.</li>' : ''}
      <li>Shipping charges extra as applicable.</li>
    </ul>

    ${(paymentTerms || bankDetails) ? `
      <div class="bank-block">
        ${paymentTerms ? `
          <div>
            <strong>Payment Terms:</strong><br/>
            ${esc(paymentTerms).replace(/\n/g,'<br/>')}
          </div>
        ` : ''}
        ${bankDetails ? `
          <div style="margin-top:6px;">
            <strong>Bank Details:</strong><br/>
            ${esc(bankDetails).replace(/\n/g,'<br/>')}
          </div>
        ` : ''}
      </div>
    ` : ''}
  </div>

  <div style="margin-top:8px;font-size:10px;color:#888;">
    Generated on ${escapeHtml(new Date().toLocaleDateString())}
  </div>
</body>
</html>`;
      }

      /* ---------- Iframe renderer ---------- */
      async function createIframeWithContent(html) {
        cleanup();
        return new Promise((resolve, reject) => {
          try {
            const iframe = document.createElement('iframe');
            iframe.setAttribute('data-pf-temp', '1');
            iframe.style.cssText =
              'position:fixed;left:0;top:0;width:210mm;height:297mm;opacity:0;pointer-events:none;z-index:2147483647;border:0';
            iframe.setAttribute('aria-hidden', 'true');
            document.body.appendChild(iframe);

            const doc = iframe.contentDocument || iframe.contentWindow.document;
            doc.open();
            doc.write(html);
            doc.close();

            (async () => {
              try { await waitForFontsInDoc(doc, 4000); } catch (e) {}
              try { await waitForImagesInDoc(doc, 5000); } catch (e) {}
              setTimeout(() => resolve({ iframe, doc }), 160);
            })();
          } catch (err) {
            reject(err);
          }
        });
      }

      /* ---------- html2canvas + jsPDF page generator ---------- */
      async function renderIframeToPdfUsingHtml2canvas(iframeDoc, filename) {
        if (!window.html2canvas) {
          await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js', 8000);
        }
        if (!window.jspdf && !window.jspdf?.jsPDF) {
          await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js', 8000);
        }

        const jsPDFCtor =
          (window.jspdf && window.jspdf.jsPDF)
            ? window.jspdf.jsPDF
            : (window.jsPDF || null);

        if (!jsPDFCtor) throw new Error('jsPDF not available');

        const body = iframeDoc.body;
        const prevOutline = body.style.outline;
        body.style.outline = 'none';

        const pxPerMm = 96 / 25.4;
        const a4WidthPx = Math.round(210 * pxPerMm);
        const scale = 2;

        const width  = body.scrollWidth || body.offsetWidth || a4WidthPx;
        const height = body.scrollHeight || body.offsetHeight || (a4WidthPx * 1.414);

        const canvas = await window.html2canvas(body, {
          scale,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          width,
          height,
          windowWidth: width,
          windowHeight: height,
          scrollX: 0,
          scrollY: 0
        });

        body.style.outline = prevOutline;

        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const pdf = new jsPDFCtor({ unit: 'mm', format: 'a4', orientation: 'portrait' });

        const pageWidthMm  = 210;
        const pageHeightMm = 297;
        const marginMm     = 8; // top/bottom page margin in mm
        const pxToMm = (px) => (px * 25.4) / 96;

        const imgWidthMm  = pageWidthMm;
        const imgHeightMm = pxToMm(canvas.height) * (pageWidthMm / pxToMm(canvas.width));

        if (imgHeightMm <= pageHeightMm) {
          pdf.addImage(imgData, 'JPEG', 0, 0, imgWidthMm, imgHeightMm);
        } else {
          const canvasWidth  = canvas.width;
          const canvasHeight = canvas.height;
          // On page 1, content starts at y=0 (body already has 8mm padding baked in)
          // On subsequent pages, we add marginMm top offset so content doesn't butt the edge
          const usableHeightMm  = pageHeightMm - marginMm; // page 2+ usable height
          const page1HeightPx   = Math.floor((pageHeightMm / imgHeightMm) * canvasHeight);
          const page2HeightPx   = Math.floor((usableHeightMm / imgHeightMm) * canvasHeight);
          let renderedY = 0;
          let pageIndex = 0;

          while (renderedY < canvasHeight) {
            const thisPageHeightPx = pageIndex === 0 ? page1HeightPx : page2HeightPx;
            const slicePx = Math.min(thisPageHeightPx, canvasHeight - renderedY);

            const pageCanvas = document.createElement('canvas');
            pageCanvas.width  = canvasWidth;
            pageCanvas.height = slicePx;
            const ctx = pageCanvas.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvasWidth, slicePx);
            ctx.drawImage(
              canvas,
              0, renderedY, canvasWidth, slicePx,
              0, 0, canvasWidth, slicePx
            );
            const pageData = pageCanvas.toDataURL('image/jpeg', 0.95);
            const sliceHeightMm =
              pxToMm(slicePx) * (pageWidthMm / pxToMm(canvas.width));
            const yOffset = pageIndex === 0 ? 0 : marginMm;

            if (pageIndex === 0) {
              pdf.addImage(pageData, 'JPEG', 0, yOffset, imgWidthMm, sliceHeightMm);
            } else {
              pdf.addPage();
              pdf.addImage(pageData, 'JPEG', 0, yOffset, imgWidthMm, sliceHeightMm);
            }
            renderedY += slicePx;
            pageIndex++;
          }
        }

        pdf.save(filename);
      }

      /* ---------- Fallback: dom-to-image-more + jsPDF ---------- */
      async function renderElementToPdfUsingDomToImage(el, filename) {
        if (!window.domtoimage) {
          await loadScript('https://cdnjs.cloudflare.com/ajax/libs/dom-to-image-more/2.9.0/dom-to-image-more.min.js', 8000);
        }
        if (!window.jspdf && !window.jspdf?.jsPDF) {
          await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js', 8000);
        }

        const jsPDFCtor =
          (window.jspdf && window.jspdf.jsPDF)
            ? window.jspdf.jsPDF
            : (window.jsPDF || null);

        if (!jsPDFCtor) throw new Error('jsPDF not available');

        const opts = {
          bgcolor: '#ffffff',
          quality: 0.98,
          width: el.scrollWidth,
          height: el.scrollHeight
        };

        const blob = await window.domtoimage.toBlob(el, opts);
        const dataUrl = await new Promise(res => {
          const fr = new FileReader();
          fr.onload = () => res(fr.result);
          fr.readAsDataURL(blob);
        });

        const img = new Image();
        img.src = dataUrl;
        await new Promise(r => { img.onload = r; img.onerror = r; });

        const pageW = 210;
        const pageH = 297;
        const marginMm = 8;
        const pdf = new jsPDFCtor({ unit: 'mm', format: 'a4', orientation: 'portrait' });

        const pxToMm = (px) => (px * 25.4) / 96;
        const imgWidthMm  = pageW;
        const imgHeightMm =
          pxToMm(img.height) * (pageW / pxToMm(img.width));

        if (imgHeightMm <= pageH) {
          pdf.addImage(dataUrl, 'PNG', 0, 0, imgWidthMm, imgHeightMm);
        } else {
          const canvas = document.createElement('canvas');
          canvas.width  = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);

          const canvasHeight = canvas.height;
          const canvasWidth  = canvas.width;
          const page1HeightPx  = Math.floor((pageH / imgHeightMm) * canvasHeight);
          const page2HeightPx  = Math.floor(((pageH - marginMm) / imgHeightMm) * canvasHeight);
          let renderedY = 0;
          let pageIndex = 0;

          while (renderedY < canvasHeight) {
            const thisPageHeightPx = pageIndex === 0 ? page1HeightPx : page2HeightPx;
            const slicePx = Math.min(thisPageHeightPx, canvasHeight - renderedY);

            const pageCanvas = document.createElement('canvas');
            pageCanvas.width  = canvasWidth;
            pageCanvas.height = slicePx;
            const pctx = pageCanvas.getContext('2d');
            pctx.fillStyle = '#ffffff';
            pctx.fillRect(0, 0, canvasWidth, slicePx);
            pctx.drawImage(
              canvas,
              0, renderedY, canvasWidth, slicePx,
              0, 0, canvasWidth, slicePx
            );
            const pageData = pageCanvas.toDataURL('image/png');
            const sliceHeightMm =
              pxToMm(slicePx) * (pageW / pxToMm(canvasWidth));
            const yOffset = pageIndex === 0 ? 0 : marginMm;

            if (pageIndex === 0) {
              pdf.addImage(pageData, 'PNG', 0, yOffset, imgWidthMm, sliceHeightMm);
            } else {
              pdf.addPage();
              pdf.addImage(pageData, 'PNG', 0, yOffset, imgWidthMm, sliceHeightMm);
            }
            renderedY += slicePx;
            pageIndex++;
          }
        }

        pdf.save(filename);
      }

      /* ---------- Public: create PDF ---------- */
      async function createPdfFromHtml(htmlContent, filename) {
        cleanup();
        let ctx;
        try {
          ctx = await createIframeWithContent(htmlContent);
        } catch (err) {
          console.error('iframe render failed', err);
          const container = document.createElement('div');
          container.setAttribute('data-pf-temp', '1');
          container.style.cssText =
            'position:absolute;left:-99999px;top:0;width:210mm;visibility:visible;background:#fff';
          container.innerHTML = htmlContent;
          document.body.appendChild(container);
          await waitForFontsInDoc(document, 2000);
          await waitForImagesInDoc(container, 3000);
          try {
            await renderElementToPdfUsingDomToImage(container, filename);
          } finally {
            container.remove();
          }
          return;
        }

        const { iframe, doc } = ctx;
        const body = doc.body;

        try {
          await renderIframeToPdfUsingHtml2canvas(doc, filename);
        } catch (err) {
          console.warn('html2canvas -> jsPDF path failed; trying dom-to-image fallback', err);
          try {
            await renderElementToPdfUsingDomToImage(body, filename);
          } catch (err2) {
            console.error('dom-to-image fallback failed', err2);
            fallbackToPrint(htmlContent);
          }
        } finally {
          try { iframe.remove(); } catch (e) {}
        }
      }

      /* ---------- Preview & copy link ---------- */
      async function previewAndCopy(htmlContent, filename) {
        cleanup();
        let ctx;
        try {
          ctx = await createIframeWithContent(htmlContent);
        } catch (err) {
          console.error('iframe render failed for preview', err);
          return fallbackToPrint(htmlContent);
        }
        const { iframe, doc } = ctx;
        const body = doc.body;

        try {
          if (!window.html2canvas) {
            await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js', 8000);
          }
          if (!window.jspdf && !window.jspdf?.jsPDF) {
            await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js', 8000);
          }
          const jsPDFCtor =
            (window.jspdf && window.jspdf.jsPDF)
              ? window.jspdf.jsPDF
              : (window.jsPDF || null);

          if (!jsPDFCtor) throw new Error('jsPDF not available');

          const width  = body.scrollWidth || body.offsetWidth;
          const height = body.scrollHeight || body.offsetHeight;

          const canvas = await window.html2canvas(body, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            width,
            height
          });

          const imgData = canvas.toDataURL('image/jpeg', 0.95);
          const pdf = new jsPDFCtor({ unit: 'mm', format: 'a4', orientation: 'portrait' });

          const pxToMm = (px) => (px * 25.4) / 96;
          const imgWidthMm  = 210;
          const pageHeightMm = 297;
          const marginMm = 8;
          const imgHeightMm =
            pxToMm(canvas.height) * (imgWidthMm / pxToMm(canvas.width));

          if (imgHeightMm <= pageHeightMm) {
            pdf.addImage(imgData, 'JPEG', 0, 0, imgWidthMm, imgHeightMm);
          } else {
            const canvasWidth  = canvas.width;
            const canvasHeight = canvas.height;
            const page1HeightPx = Math.floor((pageHeightMm / imgHeightMm) * canvasHeight);
            const page2HeightPx = Math.floor(((pageHeightMm - marginMm) / imgHeightMm) * canvasHeight);
            let renderedY = 0;
            let pageIndex = 0;

            while (renderedY < canvasHeight) {
              const thisPageHeightPx = pageIndex === 0 ? page1HeightPx : page2HeightPx;
              const slicePx = Math.min(thisPageHeightPx, canvasHeight - renderedY);

              const pageCanvas = document.createElement('canvas');
              pageCanvas.width  = canvasWidth;
              pageCanvas.height = slicePx;
              const ctx2 = pageCanvas.getContext('2d');
              ctx2.fillStyle = '#ffffff';
              ctx2.fillRect(0, 0, canvasWidth, slicePx);
              ctx2.drawImage(
                canvas,
                0, renderedY, canvasWidth, slicePx,
                0, 0, canvasWidth, slicePx
              );
              const pageData = pageCanvas.toDataURL('image/jpeg', 0.95);
              const sliceHeightMm =
                pxToMm(slicePx) * (imgWidthMm / pxToMm(canvasWidth));
              const yOffset = pageIndex === 0 ? 0 : marginMm;

              if (pageIndex === 0) {
                pdf.addImage(pageData, 'JPEG', 0, yOffset, imgWidthMm, sliceHeightMm);
              } else {
                pdf.addPage();
                pdf.addImage(pageData, 'JPEG', 0, yOffset, imgWidthMm, sliceHeightMm);
              }
              renderedY += slicePx;
              pageIndex++;
            }
          }

          const blob = pdf.output('blob');
          if (!blob) throw new Error('Failed to create preview blob');

          if (window.__pf_lastObjectURL) {
            try { URL.revokeObjectURL(window.__pf_lastObjectURL); } catch (e) {}
            window.__pf_lastObjectURL = null;
          }

          const url = URL.createObjectURL(blob);
          window.__pf_lastObjectURL = url;
          window.open(url, '_blank');

          try {
            await navigator.clipboard.writeText(url);
            alert('Preview opened — object URL copied to clipboard.');
          } catch (err) {
            const t = document.createElement('textarea');
            t.value = url;
            document.body.appendChild(t);
            t.select();
            try {
              document.execCommand('copy');
              alert('Preview opened — object URL copied (execCommand).');
            } catch (e) {
              window.prompt('Preview URL (copy manually):', url);
            }
            t.remove();
          }

          setTimeout(() => {
            try { URL.revokeObjectURL(window.__pf_lastObjectURL); } catch (e) {}
            window.__pf_lastObjectURL = null;
          }, 10 * 60 * 1000);
        } catch (err) {
          console.error('Preview generation failed, falling back to print', err);
          fallbackToPrint(htmlContent);
        } finally {
          try { iframe.remove(); } catch (e) {}
        }
      }

      // fallback: simple print window
      function fallbackToPrint(htmlContent) {
        const w = window.open('', '_blank', 'width=900,height=600');
        if (!w) return alert('Please allow popups for printing');
        w.document.write(
          `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>Proforma Invoice</title>
  <style>
    body{font-family:Arial,Helvetica,sans-serif;padding:12mm;}
    @media print{ @page{margin:12mm} }
  </style>
</head>
<body>
  ${htmlContent}
  <div style="margin-top:12px;">
    <button onclick="window.print()">Print / Save as PDF</button>
    <button onclick="window.close()">Close</button>
  </div>
</body>
</html>`
        );
        w.document.close();
      }

      /* ---------- Button handlers ---------- */
      createBtn.onclick = async () => {
        if (!isAuthed())       return alert('Access code required.');
        if (!validateCustomer()) return;
        if (!validateItems())    return;

        const cust = {
          name:    $('pf_customer_name')?.value.trim()    || '',
          company: $('pf_customer_company')?.value.trim() || '',
          address: $('pf_customer_address')?.value.trim() || '',
          phone:   $('pf_customer_phone')?.value.trim()   || '',
          email:   $('pf_customer_email')?.value.trim()   || '',
          gst:     $('pf_customer_gst')?.value.trim()     || '',
          country: $('pf_customer_country')?.value        || ''
        };

        const notes               = $('pf-notes')?.value.trim()           || '';
        const proformaNumber      = proformaNoInput?.value.trim()         || '';
        const proformaDate        = proformaDateInput?.value              || '';
        const bankDetails         = bankDetailsInput?.value.trim()        || '';
        const paymentTerms        = paymentTermsInput?.value.trim()       || '';
        const discountPercentNote = discountPctInput?.value.trim()        || '';

        const original = createBtn.textContent;
        createBtn.disabled = true;
        createBtn.textContent = 'Generating PDF... Please wait.';

        const gstType = getSelectedGstType();

        try {
          const html = buildPrintableNode(cust, items, notes, currentCurrency, {
            proformaNumber,
            proformaDate,
            bankDetails,
            paymentTerms,
            discountAmountOpt: discountAmount,
            discountPercentNote,
            gstType
          });
          const safeName =
            (cust.name || 'customer').replace(/[^\w\-]/g, '_').slice(0, 60);
          await createPdfFromHtml(html, `Proforma_Invoice_${safeName}_${Date.now()}.pdf`);
        } catch (err) {
          console.error('Final PDF creation error:', err);
          const fallbackCust = $('pf_customer_name')
            ? { name: $('pf_customer_name').value }
            : { name: 'Customer' };
          fallbackToPrint(
            buildPrintableNode(
              fallbackCust,
              items,
              $('pf-notes')?.value || '',
              currentCurrency,
              {
                proformaNumber,
                proformaDate,
                bankDetails,
                paymentTerms,
                discountAmountOpt: discountAmount,
                discountPercentNote,
                gstType
              }
            )
          );
        } finally {
          createBtn.disabled = false;
          createBtn.textContent = original;
          cleanup();
        }
      };

      if (previewBtn) {
        previewBtn.onclick = async () => {
          if (!isAuthed())       return alert('Access code required.');
          if (!validateCustomer()) return;
          if (!validateItems())    return;

          const cust = {
            name:    $('pf_customer_name')?.value.trim()    || '',
            company: $('pf_customer_company')?.value.trim() || '',
            address: $('pf_customer_address')?.value.trim() || '',
            phone:   $('pf_customer_phone')?.value.trim()   || '',
            email:   $('pf_customer_email')?.value.trim()   || '',
            gst:     $('pf_customer_gst')?.value.trim()     || '',
            country: $('pf_customer_country')?.value        || ''
          };

          const notes               = $('pf-notes')?.value.trim()           || '';
          const proformaNumber      = proformaNoInput?.value.trim()         || '';
          const proformaDate        = proformaDateInput?.value              || '';
          const bankDetails         = bankDetailsInput?.value.trim()        || '';
          const paymentTerms        = paymentTermsInput?.value.trim()       || '';
          const discountPercentNote = discountPctInput?.value.trim()        || '';

          const gstType = getSelectedGstType();

          const html = buildPrintableNode(cust, items, notes, currentCurrency, {
            proformaNumber,
            proformaDate,
            bankDetails,
            paymentTerms,
            discountAmountOpt: discountAmount,
            discountPercentNote,
            gstType
          });

          const safeName =
            (cust.name || 'customer').replace(/[^\w\-]/g, '_').slice(0, 60);
          previewBtn.disabled = true;
          previewBtn.textContent = 'Generating preview...';
          try {
            await previewAndCopy(html, `Proforma_Invoice_${safeName}_${Date.now()}.pdf`);
          } catch (err) {
            console.error('Preview error', err);
            fallbackToPrint(html);
          } finally {
            previewBtn.disabled = false;
            previewBtn.textContent = 'Preview & Copy Link';
            cleanup();
          }
        };
      }

      // Auth
      authBtn.onclick = () => {
        const code = codeInput?.value.trim() || '';
        if (authMsg) {
          authMsg.textContent = '';
          authMsg.style.color = '#d32f2f';
        }
        if (!code) {
          if (authMsg) authMsg.textContent = 'Enter access code.';
          return;
        }
        if (ALLOWED_CODES.includes(code)) {
          setAuthed();
          applyAuthState();
          if (authMsg) {
            authMsg.style.color = '#2d8f3f';
            authMsg.textContent = 'Access Granted';
            setTimeout(() => {
              if (authMsg) {
                authMsg.textContent = '';
                authMsg.style.color = '#d32f2f';
              }
            }, 2000);
          }
        } else {
          if (authMsg) authMsg.textContent = 'Invalid Code. Access Denied.';
        }
      };

      if (logoutBtn) {
        logoutBtn.onclick = () => {
          clearAuth();
          applyAuthState();
          if (codeInput) codeInput.value = '';
        };
      }

      // init UI visibility
      applyAuthState();
    } catch (err) {
      console.error('proforma-invoice initialization error:', err);
    }
  } // end initProformaInvoice

  // Run init immediately or on DOMContentLoaded depending on state
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProformaInvoice);
  } else {
    initProformaInvoice();
  }

})();